import type { StorageService } from '@/services/storage/types';

import { isBrowser, safeDecodeURIComponent } from './utils';

export type CookieWriteOptions = {
  name: string;
  value: string;
  path?: string;
  domain?: 'auto' | string;
  sameSite?: 'Lax' | 'Strict' | 'None';
  secure?: boolean;
  maxAgeMs?: number;
};

/**
 * Persistência de managed params — storage + cookie SSR-safe.
 * Não acessa DOM/localStorage em import time.
 */
export class UrlContextStore {
  private readonly storage: StorageService | null;

  constructor(storage?: StorageService | null) {
    this.storage = storage ?? null;
  }

  getStorage(key: string): string | null {
    if (!this.storage) return null;
    try {
      const value = this.storage.get<string>(key);
      return typeof value === 'string' && value.length > 0 ? value : null;
    } catch {
      return null;
    }
  }

  setStorage(key: string, value: string): void {
    if (!this.storage) return;
    try {
      this.storage.set(key, value);
    } catch {
      // quota / private mode
    }
  }

  getCookie(name: string): string | null {
    if (!isBrowser()) return null;
    try {
      const cookies = document.cookie ? document.cookie.split(';') : [];
      for (const part of cookies) {
        const idx = part.indexOf('=');
        const key = (idx >= 0 ? part.slice(0, idx) : part).trim();
        if (key !== name) continue;
        const raw = idx >= 0 ? part.slice(idx + 1).trim() : '';
        return safeDecodeURIComponent(raw);
      }
    } catch {
      return null;
    }
    return null;
  }

  setCookie(options: CookieWriteOptions): void {
    if (!isBrowser()) return;

    const parts = [
      `${encodeURIComponent(options.name)}=${encodeURIComponent(options.value)}`,
    ];

    parts.push(`path=${options.path ?? '/'}`);

    const domain = resolveCookieDomain(options.domain);
    if (domain) {
      parts.push(`domain=${domain}`);
    }

    if (
      typeof options.maxAgeMs === 'number' &&
      Number.isFinite(options.maxAgeMs)
    ) {
      parts.push(`max-age=${Math.max(0, Math.floor(options.maxAgeMs / 1000))}`);
    }

    const sameSite = options.sameSite ?? 'Lax';
    parts.push(`SameSite=${sameSite}`);

    const secure =
      options.secure === true ||
      (typeof location !== 'undefined' && location.protocol === 'https:');
    if (secure) {
      parts.push('Secure');
    }

    try {
      document.cookie = parts.join('; ');
    } catch {
      // ignore
    }
  }
}

function resolveCookieDomain(
  domain: 'auto' | string | undefined
): string | null {
  if (!domain || domain === 'auto') {
    if (!isBrowser()) return null;
    const host = location.hostname;
    // localhost / IPs: não forçar domain
    if (
      host === 'localhost' ||
      host === '127.0.0.1' ||
      /^\d+\.\d+\.\d+\.\d+$/.test(host) ||
      !host.includes('.')
    ) {
      return null;
    }
    return host;
  }
  return domain;
}
